import { KeyboardEvent, useEffect, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  Divider,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomButton from "../CustomButton";
import ConfirmationModal from "../misc/ConfirmationModal";
import themeConfig from "../../theme/themeConfig.json";
import { ActionMenuItem, ActionMenuProps } from "./actionMenu.interface";
import ActionMenuOption from "./ActionMenuOption";
import ActionMenuButton from "./ActionMenuButton";

export type {
  ActionMenuItem,
  ActionMenuKind,
  ActionMenuProps,
  ActionMenuSection,
} from "./actionMenu.interface";

let nextMenuId = 0;

function needsDivider(item: ActionMenuItem, previous?: ActionMenuItem) {
  if (!previous) return false;
  return Boolean(
    item.dividerBefore ||
      (item.section && previous.section && item.section !== previous.section),
  );
}

function ActionMenu({
  items,
  title,
  tooltipSize = "compact",
  minItemsForMenu,
  ariaLabel = "Actiuni",
  disabled = false,
}: ActionMenuProps) {
  const navigate = useNavigate();
  const [menuId] = useState(() => `action-menu-${++nextMenuId}`);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const [confirmationItem, setConfirmationItem] =
    useState<ActionMenuItem | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const confirmationTriggerRef = useRef<HTMLElement | null>(null);
  const visibleItems = items.filter((item) => !item.hidden);
  const hasVisibleItems = visibleItems.length > 0;
  const showInlineButtons =
    minItemsForMenu !== undefined && visibleItems.length < minItemsForMenu;
  const isLoading = visibleItems.some((item) => item.loading);
  const isBusy = disabled || isLoading;
  const isOpen = Boolean(anchorEl) && !showInlineButtons;
  const displayItems = isLoading
    ? visibleItems.map((item) => ({
        ...item,
        tooltip: item.loading
          ? "Actiunea este in curs."
          : "Asteapta finalizarea actiunii in curs.",
      }))
    : visibleItems;

  useEffect(() => {
    setAnchorEl(null);
    setActiveTooltipId(null);
  }, [showInlineButtons, hasVisibleItems]);

  useEffect(() => {
    if (!confirmationItem && confirmationTriggerRef.current) {
      const trigger = confirmationTriggerRef.current;
      confirmationTriggerRef.current = null;
      if (document.activeElement === document.body) trigger.focus();
    }
  }, [confirmationItem]);

  const handleClose = (restoreFocus = false) => {
    setAnchorEl(null);
    setActiveTooltipId(null);
    if (restoreFocus) triggerRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === "Escape" || event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();
      handleClose(true);
    }
  };

  const handleSelect = (item: ActionMenuItem, source?: HTMLElement) => {
    if (isBusy || item.disabled || item.loading) return;
    if (source) triggerRef.current = source;
    handleClose(!item.withConfirmationModal && !item.navigate);
    if (item.withConfirmationModal) {
      confirmationTriggerRef.current = triggerRef.current;
      setConfirmationItem(item);
      return;
    }
    // Ruleaza direct din click, inclusiv actiunile care deschid o fereastra.
    item.onClick?.();
  };

  const handleConfirmationClose = () => {
    setConfirmationItem(null);
  };

  const handleConfirm = () => {
    const item = items.find((item) => item.id === confirmationItem?.id);
    if (!item || item.hidden || item.disabled || item.loading || isBusy) return;
    const callback =
      item.withConfirmationModal?.modalRightButtonOnClick ?? item.onClick;
    callback?.();
    if (item.navigate) navigate(item.navigate);
  };
  const confirmation = confirmationItem?.withConfirmationModal;

  if (!visibleItems.length) return null;

  return (
    <>
      {showInlineButtons ? (
        <div
          className="flex flex-wrap justify-end gap-3"
          role="group"
          aria-label={ariaLabel}>
          {displayItems.map((item) => (
            <ActionMenuButton
              key={item.id}
              item={item}
              menuRef={menuRef}
              tooltipOpen={activeTooltipId === item.id}
              onTooltipOpen={() => setActiveTooltipId(item.id)}
              onTooltipClose={() =>
                setActiveTooltipId((current) =>
                  current === item.id ? null : current,
                )
              }
              disabled={isBusy || item.disabled || item.loading}
              onSelect={handleSelect}
            />
          ))}
        </div>
      ) : (
        <CustomButton
          variant="outlined"
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-controls={isOpen ? menuId : undefined}
          aria-expanded={isOpen}
          disabled={disabled}
          loading={isLoading}
          onClick={(event) => {
            triggerRef.current = event.currentTarget;
            setAnchorEl(isOpen ? null : event.currentTarget);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              triggerRef.current = event.currentTarget;
              setAnchorEl(event.currentTarget);
            }
          }}>
          <span
            aria-hidden="true"
            className="text-lg leading-none tracking-wider">
            ...
          </span>
        </CustomButton>
      )}
      {isOpen && (
        <Popper
          open
          role="presentation"
          anchorEl={anchorEl}
          placement="bottom-end"
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
          modifiers={[
            { name: "offset", options: { offset: [0, 8] } },
            {
              name: "flip",
              options: {
                fallbackPlacements: ["top-end", "bottom-start", "top-start"],
                padding: 8,
              },
            },
            {
              name: "preventOverflow",
              options: { padding: 8, altAxis: true },
            },
          ]}>
          <ClickAwayListener onClickAway={() => handleClose()}>
            <Paper
              ref={menuRef}
              elevation={0}
              sx={{
                width: 290,
                maxWidth: "calc(100vw - 16px)",
                maxHeight: "calc(100vh - 16px)",
                display: "flex",
                flexDirection: "column",
                border: "1px solid #E2E8F0",
                borderRadius: "10px",
                boxShadow: themeConfig.shadows.card,
                overflow: "hidden",
              }}>
              {title && (
                <Box
                  sx={{
                    px: 2.25,
                    py: 1.5,
                    borderBottom: "1px solid #E2E8F0",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    lineHeight: 1.5,
                    flexShrink: 0,
                    overflowWrap: "anywhere",
                  }}>
                  {title}
                </Box>
              )}
              <MenuList
                id={menuId}
                aria-label={ariaLabel}
                aria-busy={isLoading || undefined}
                autoFocus
                autoFocusItem={false}
                disabledItemsFocusable
                variant="menu"
                onKeyDown={handleKeyDown}
                sx={{
                  p: 0.75,
                  minHeight: 0,
                  maxHeight: "calc(100vh - 24px)",
                  overflowY: "auto",
                }}>
                {displayItems.flatMap((item, index) => [
                  ...(needsDivider(item, displayItems[index - 1])
                    ? [
                        <Divider
                          key={`divider-${item.id}`}
                          component="li"
                          sx={{ my: 0.75, mx: 1.5, borderColor: "#E2E8F0" }}
                        />,
                      ]
                    : []),
                  <ActionMenuOption
                    key={`item-${item.id}`}
                    item={item}
                    menuRef={menuRef}
                    tooltipSize={tooltipSize}
                    tooltipOpen={activeTooltipId === item.id}
                    onTooltipOpen={() => setActiveTooltipId(item.id)}
                    onTooltipClose={() =>
                      setActiveTooltipId((current) =>
                        current === item.id ? null : current,
                      )
                    }
                    disabled={isBusy || item.disabled || item.loading}
                    onSelect={handleSelect}
                  />,
                ])}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
      {confirmation && (
        <ConfirmationModal
          show
          handleClose={handleConfirmationClose}
          title={confirmation.modalTitle}
          subtitle={confirmation.modalSubtitle}
          leftButtonText={confirmation.modalLeftButtonText}
          rightButtonText={confirmation.modalRightButtonText}
          leftButtonOnClick={confirmation.modalLeftButtonOnClick}
          rightButtonOnClick={handleConfirm}
          leftButtonColor={confirmation.modalLeftButtonColor}
          rightButtonColor={confirmation.modalRightButtonColor}
          leftButtonVariant={confirmation.modalLeftButtonVariant}
          rightButtonVariant={confirmation.modalRightButtonVariant}
          modalSize={confirmation.modalSize}
        />
      )}
    </>
  );
}

export default ActionMenu;
